import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Motion";
import Faq from "@/components/Faq";
import PageSeo from "@/components/PageSeo";
import { GUIDES, guidePath } from "@/data/guides_index";
import { formatDateFr } from "@/lib/format";
import { hy } from "@/lib/hyphenate";

const GROUPS = [
  { title: "Pour les artisans et entreprises du bâtiment", audiences: ["artisan"] },
  { title: "Pour les clients et maîtres d’ouvrage", audiences: ["tous", "client"] },
];

const FAQ = [
  {
    q: "Qu'est-ce que la garantie décennale ?",
    a: "Issue de la loi Spinetta du 4 janvier 1978 (Code civil, articles 1792 et suivants), la responsabilité décennale pèse sur les constructeurs pendant dix ans à compter de la réception des travaux, pour les dommages qui compromettent la solidité de l'ouvrage ou le rendent impropre à sa destination. L'assurance de responsabilité décennale (Code des assurances, article L241-1) permet au professionnel de couvrir cette responsabilité ; elle couvre la responsabilité du constructeur dans les conditions légales et contractuelles. Le préfinancement des réparations sans recherche préalable des responsabilités relève de l’assurance dommages-ouvrage du maître d’ouvrage.",
  },
  {
    q: "Qui doit souscrire une assurance décennale ?",
    a: "Les professionnels dont la responsabilité décennale peut être engagée au titre des articles 1792 et suivants du Code civil doivent être couverts avant l'ouverture des chantiers concernés — gros œuvre comme second œuvre, dès le premier chantier, y compris en création d'entreprise ou en micro-entreprise. Cas particulier : le sous-traitant n'est pas directement lié au maître d'ouvrage et n'entre pas, à ce titre, dans le champ de la garantie décennale légale de la même manière que l'entreprise principale ; il reste toutefois responsable de ses engagements envers l'entreprise qui lui a confié les travaux.",
  },
  {
    q: "Quelle différence entre RC Pro et RC décennale ?",
    a: "La RC Pro et la responsabilité civile exploitation peuvent intervenir pour des dommages causés à des tiers, dans les limites du contrat. Les garanties pendant les travaux, après livraison et sur les biens existants doivent être vérifiées : tous les dommages ne sont pas automatiquement couverts. La RC décennale répond à la responsabilité légale du constructeur pour des dommages graves après réception. Ces garanties sont complémentaires et peuvent être étudiées dans une même demande.",
  },
  {
    q: "Que risque un professionnel non assuré ?",
    a: "Le professionnel soumis à l'obligation d'assurance décennale doit être assuré avant l'ouverture du chantier concerné. À défaut, il peut devoir supporter les réparations engageant sa responsabilité. L'article L243-3 du Code des assurances prévoit également des sanctions pouvant atteindre six mois d'emprisonnement et 75 000 € d'amende.",
  },
  {
    q: "Je crée ma société : puis-je être assuré ?",
    a: "Une entreprise en création peut demander une étude. L'assureur examine notamment les activités, l'expérience du dirigeant, les justificatifs et le chiffre d'affaires prévisionnel. Une acceptation ou un tarif ne peuvent pas être garantis à l'avance. Attendez la confirmation de la prise d'effet avant d'ouvrir un chantier soumis à l'obligation d'assurance.",
  },
  {
    q: "Je suis sans décennale depuis plus de 6 mois, est-ce un problème ?",
    a: "Déclarez la durée et la raison de l'interruption, ainsi que les éventuels chantiers réalisés pendant cette période. L'assureur décide de l'acceptation du dossier et des conditions proposées après étude. Un nouveau contrat ne couvre pas automatiquement les chantiers déjà ouverts sans assurance.",
  },
  {
    q: "Quels sont les critères qui déterminent le tarif ?",
    a: "La prime dépend principalement de l'activité exercée, de l'expérience, des diplômes et certifications, des antécédents de sinistres, du chiffre d'affaires et du nombre de salariés — ainsi que d'autres critères (zone géographique, type de chantiers, niveau de garanties, franchises). Consultez notre page Prix & tarifs pour les fourchettes indicatives par métier.",
  },
  {
    q: "Comment obtenir mon attestation décennale ?",
    a: "Après acceptation du dossier et réalisation des conditions de souscription, l'assureur confirme la couverture et délivre l'attestation selon ses modalités. Vérifiez les activités, la période de validité, les techniques admises et la zone géographique. Elle doit être remise au client avant l'ouverture du chantier et jointe aux devis et factures. Une demande de devis ne vaut ni contrat ni attestation.",
  },
  {
    q: "Mon métier déclaré suffit-il à couvrir toutes mes activités ?",
    a: "Non. Le contrat doit correspondre aux travaux réellement effectués, y compris les activités secondaires, les matériaux et les procédés employés. Par exemple, la plomberie, le chauffage et l'installation d'une pompe à chaleur doivent être décrits précisément. Consultez le libellé des activités garanties et demandez confirmation à l'assureur avant une nouvelle intervention.",
  },
  {
    q: "Quelle assurance vérifier quand je travaille en sous-traitance ?",
    a: "Sans contrat direct avec le maître d'ouvrage, le sous-traitant n'est pas soumis à la responsabilité décennale légale envers celui-ci dans les mêmes conditions que l'entreprise principale. Il reste responsable envers l'entreprise qui lui confie le chantier. Vérifiez les garanties de responsabilité en qualité de sous-traitant et les exigences du marché : une RC Pro générale ne suffit pas nécessairement.",
  },
  {
    q: "Un équipement remplacé sur un bâtiment existant relève-t-il de la décennale ?",
    a: "Pas automatiquement. Depuis l'arrêt du 21 mars 2024, la Cour de cassation précise que l'équipement ajouté ou remplacé sur un ouvrage existant, sans constituer lui-même un ouvrage, relève de la responsabilité contractuelle de droit commun et non des garanties décennale ou biennale. La nature de l'intervention doit donc être décrite à l'assureur pour vérifier la couverture correspondante.",
  },
];

const GUIDE_FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

export default function Guide() {
  return (
    <div data-testid="guide-page">
      <PageSeo path="/guide" jsonLd={GUIDE_FAQ_LD} />
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 py-16 lg:py-24">
        <Reveal y={12}>
          <SectionTag className="mb-4">Guide décennale</SectionTag>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase tracking-tighter leading-[0.9] mb-6">
            {hy("Guides de l’assurance")}<br /><span className="text-accent">{hy("décennale.")}</span>
          </h1>
          <p className="max-w-2xl text-base sm:text-lg leading-relaxed text-ink/75 mb-6">
            Création d'entreprise, refus d'assurance et BCT, attestation sur les devis,
            résiliation, cessation d'activité, sous-traitance : des guides pratiques rédigés à
            partir des textes officiels, pour décider en connaissance de cause.
          </p>
          <p className="mb-12 font-mono text-[11px] uppercase tracking-[0.18em] text-ink/60">
            Informations revues le <time dateTime="2026-09-29">{formatDateFr("2026-09-29")}</time>
          </p>
        </Reveal>

        {GROUPS.map((group) => (
          <section key={group.title} className="mb-16" aria-label={group.title}>
            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight mb-6">{group.title}</h2>
            <ul className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {GUIDES.filter((g) => group.audiences.includes(g.audience)).map((guide) => (
                <li key={guide.slug}>
                  <Link to={guidePath(guide.slug)} className="group flex h-full flex-col border border-ink/25 bg-white p-6 hover:border-accent">
                    <h3 className="font-display text-xl font-bold mb-3 group-hover:text-accent">{guide.h1}</h3>
                    <p className="text-sm leading-relaxed text-ink/75 mb-4">{guide.desc}</p>
                    <span className="mt-auto flex items-center gap-2 text-sm font-bold">Lire le guide <ArrowUpRight aria-hidden="true" className="h-4 w-4" /></span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}

        <h2 className="font-display text-3xl font-bold mb-8">Les réponses aux questions courantes</h2>
        <div className="mb-20">
          <Faq items={FAQ} prefix="guide-faq" />
        </div>

        <Reveal>
          <section className="mb-16 border-t-2 border-ink pt-8" aria-labelledby="guide-sources-title">
            <h2 id="guide-sources-title" className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tighter mb-4">
              Sources officielles et points à vérifier
            </h2>
            <p className="max-w-3xl text-sm leading-relaxed text-ink/75 mb-6">
              Ces réponses s'appuient sur les références suivantes. Pour votre chantier,
              vérifiez également les activités garanties et les conditions de votre contrat :
              une information générale ne confirme pas à elle seule une prise en charge.
            </p>
            <ul className="max-w-3xl space-y-4 text-sm leading-relaxed text-ink/75">
              <li>
                <a href="https://entreprendre.service-public.gouv.fr/vosdroits/F2034" className="underline underline-offset-4 hover:text-accent">
                  Service Public : garantie décennale des constructeurs
                </a>
                {" — obligation, durée, sous-traitance et travaux déclarés."}
              </li>
              <li>
                <a href="https://www.service-public.gouv.fr/particuliers/vosdroits/R44868" className="underline underline-offset-4 hover:text-accent">
                  Service Public : modèle d'attestation individuelle
                </a>
                {" — activités, période, techniques et périmètre du chantier."}
              </li>
              <li>
                <a href="https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000031010272" className="underline underline-offset-4 hover:text-accent">
                  Code des assurances, article L243-2
                </a>
                {" — justification de l'assurance et attestation jointe aux devis et factures."}
              </li>
              <li>
                <a href="https://www.courdecassation.fr/publications/lettre-de-la-troisieme-chambre-civile/ndeg13-mars-2024/construction" className="underline underline-offset-4 hover:text-accent">
                  Cour de cassation : équipements ajoutés ou remplacés sur l'existant
                </a>
                {" — portée de l'arrêt du 21 mars 2024, pourvoi n° 22-18.694."}
              </li>
            </ul>
          </section>
        </Reveal>

        <Reveal className="flex flex-wrap items-center justify-between gap-6 border-2 border-ink bg-white p-8 sm:p-12 shadow-brutal">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tighter">
              Prêt à passer au concret ?
            </h2>
            <p className="mt-2 text-sm text-ink/70 max-w-xl">
              Décrivez votre activité pour une étude gratuite et sans engagement de souscription.
            </p>
          </div>
          <Link
            to="/devis"
            data-testid="guide-cta-devis"
            className="inline-flex items-center gap-2 border-2 border-ink bg-accent px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors hover:bg-ink"
          >
            Demander un devis
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </div>
  );
}
